import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ModuleLayout, ModuleDataTable } from "../Common/ModuleLayout";
import { projectService } from "../../services/projectService";
import { pointService } from "../../services/pointService";
import { siteService } from "../../services/siteService";
import { deviceTypeService } from "../../services/deviceTypeService";
import { roomTypeService } from "../../services/roomTypeService";
import { pointTypeService } from "../../services/pointTypeService";
import ProjectsFilter from "./ProjectsFilter";
import ProjectsPagination from "./ProjectsPagination";
import ProjectModal from "./ProjectModal";
import PointsFilter from "./PointsFilter";
import PointsPagination from "./PointsPagination";
import PointModal from "./PointModal";
import SitesFilter from "./SitesFilter";
import SitesPagination from "./SitesPagination";
import SiteModal from "./SiteModal";
import DeviceTypesFilter from "./DeviceTypesFilter";
import DeviceTypeModal from "./DeviceTypeModal";
import RoomTypesFilter from "./RoomTypesFilter";
import RoomTypeModal from "./RoomTypeModal";
import { toCamelCase } from "../../util/string";
import { getCurrentUserType } from "../../util/auth";
import { devicesManagementColumnDefinitions } from "../../Data/devicesManagementColumns";
import { DEVICES_MANAGEMENT_VIEWS_STRUCTURE } from "../../Data/devicesManagementViews";
import PointTypeModal from "./PointTypeModal";
import PointTypesFilter from "./PointTypesFilter";
import ConfirmDeleteDialog from "../Common/ConfirmDeleteDialog";

/**
 * DevicesManagement Module with Tree Structure
 * Categories: Projects (My Projects), Points (All Points), Sites (All Sites)
 */
const DevicesManagementModule = () => {
  const [searchParams] = useSearchParams();
  const urlView = searchParams.get('view');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    myProjects: [],
    allPoints: [],
    allSites: [],
    allDeviceTypes: [],
    allRoomTypes: [],
    allPointTypes: [],
  });
  const [currentView, setCurrentView] = useState(null);

  // Pagination states for each view
  const [pagination, setPagination] = useState({
    myProjects: { currentPage: 0, pageSize: 10, totalPages: 0, totalElements: 0 },
    allPoints: { currentPage: 0, pageSize: 10, totalPages: 0, totalElements: 0 },
    allSites: { currentPage: 0, pageSize: 20, totalPages: 0, totalElements: 0 },
    allDeviceTypes: { currentPage: 0, pageSize: 20, totalPages: 0, totalElements: 0 },
    allRoomTypes: { currentPage: 0, pageSize: 20, totalPages: 0, totalElements: 0 },
    allPointTypes: { currentPage: 0, pageSize: 20, totalPages: 0, totalElements: 0 },
  });

  // Filter states for each view
  const [filters, setFilters] = useState({
    myProjects: { name: "", status: null, sort: "-createdAt" },
    allPoints: { projId: "", projName: "", pointDisplayName: "", sortBy: "pointId", sortDir: "asc" },
    allSites: { name: "", sortBy: "createdAt", sortDir: "desc" },
    allDeviceTypes: { name: "" },
    allRoomTypes: { name: "" },
    allPointTypes: { name: "" },
  });

  // Modal states
  const [projectModalState, setProjectModalState] = useState({
    isOpen: false,
    mode: "create",
    selectedProject: null,
  });

  const [pointModalState, setPointModalState] = useState({
    isOpen: false,
    mode: "create",
    selectedPoint: null,
  });

  const [siteModalState, setSiteModalState] = useState({
    isOpen: false,
    mode: "create",
    selectedSite: null,
  });

  const [deviceTypeModalState, setDeviceTypeModalState] = useState({
    isOpen: false,
    mode: "create",
    selectedDeviceType: null,
  });

  const [roomTypeModalState, setRoomTypeModalState] = useState({
    isOpen: false,
    mode: "create",
    selectedRoomType: null,
  });

  const [pointTypeModalState, setPointTypeModalState] = useState({
    isOpen: false,
    mode: "create",
    selectedPointType: null,
  });

  const [pendingDelete, setPendingDelete] = useState({ viewId: null, row: null, deleting: false });

  // Per-project sync loading state: Set of projId strings currently syncing
  const [syncingProjectIds, setSyncingProjectIds] = useState(new Set());

  const handleSyncProject = async (row) => {
    const projId = row.projId;
    if (!projId || syncingProjectIds.has(projId)) return;
    setSyncingProjectIds((prev) => new Set([...prev, projId]));
    try {
      const result = await projectService.syncProjectPoints(projId);
      if (result?.status === "FAILED") {
        toast.error(`Sync failed for "${row.recDisplayName || projId}": ${result.errorMessage || "Unknown error"}`);
      } else {
        const summary = result
          ? `${result.newRecords ?? 0} new, ${result.updatedRecords ?? 0} updated`
          : "done";
        toast.success(`Points synced for "${row.recDisplayName || projId}" — ${summary}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to sync points for "${row.recDisplayName || projId}"`);
    } finally {
      setSyncingProjectIds((prev) => {
        const next = new Set(prev);
        next.delete(projId);
        return next;
      });
    }
  };

  // Column definitions (extracted to Data/devicesManagementColumns.js)
  const columnDefinitions = devicesManagementColumnDefinitions;

  // Static views structure (extracted to Data/devicesManagementViews.js)
  const viewsStructure = DEVICES_MANAGEMENT_VIEWS_STRUCTURE;

  // Enrich views with counts (doesn't trigger layout reset because ModuleLayout checks view IDs, not reference)
  const views = useMemo(() => {
    return viewsStructure.map((category) => {
      // Calculate category total count
      const categoryCount = category.subViews.reduce((sum, subView) => {
        const viewKey = toCamelCase(subView.id);
        return sum + (data[viewKey]?.length || 0);
      }, 0);

      // Add counts to subviews
      const enrichedSubViews = category.subViews.map((subView) => {
        const viewKey = toCamelCase(subView.id);
        return {
          ...subView,
          count: data[viewKey]?.length || 0,
        };
      });

      return {
        ...category,
        count: categoryCount,
        subViews: enrichedSubViews,
      };
    });
  }, [data, viewsStructure]);

  // Sync currentView from URL — avoids calling setState inside renderContent (during render)
  useEffect(() => {
    setCurrentView(urlView || "my-projects");
  }, [urlView]);

  // Fetch data when view changes
  useEffect(() => {
    if (currentView) {
      fetchViewData(currentView);
    }
  }, [currentView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generic fetch function that routes to specific endpoints
  const fetchViewData = async (viewId, page = null, size = null, viewFilters = null) => {
    const viewKey = toCamelCase(viewId);
    const currentPage = page !== null ? page : pagination[viewKey]?.currentPage || 0;
    const currentSize = size !== null ? size : pagination[viewKey]?.pageSize || 10;
    const currentFilters = viewFilters || filters[viewKey];

    setLoading(true);
    try {
      const base = { page: currentPage, size: currentSize };
      let response;

      if (viewId === "my-projects") {
        response = await projectService.list({
          ...base,
          ...(currentFilters.sort && { sort: currentFilters.sort }),
          ...(currentFilters.name?.trim() && { name: currentFilters.name.trim() }),
          ...(currentFilters.status && { status: currentFilters.status }),
        });
      } else if (viewId === "all-points") {
        response = await pointService.search({
          ...base,
          projId: currentFilters.projId?.trim() || "",
          projName: currentFilters.projName?.trim() || "",
          pointDisplayName: currentFilters.pointDisplayName?.trim() || "",
          sortBy: currentFilters.sortBy || "pointId",
          sortDir: currentFilters.sortDir || "asc",
        });
      } else if (viewId === "all-sites") {
        response = await siteService.search({
          ...base,
          sortDir: currentFilters.sortDir || "desc",
          sortBy: currentFilters.sortBy || "createdAt",
          name: currentFilters.name?.trim() || "",
        });
      } else if (viewId === "all-device-types") {
        response = await deviceTypeService.search({
          ...base,
          name: currentFilters.name?.trim() || "",
        });
      } else if (viewId === "all-room-types") {
        response = await roomTypeService.search({
          ...base,
          name: currentFilters.name?.trim() || "",
        });
      } else if (viewId === "all-point-types") {
        response = await pointTypeService.search({
          ...base,
          name: currentFilters.name?.trim() || "",
        });
      }

      const items = response.content || [];
      const paginationData = {
        currentPage: response.number || 0,
        pageSize: response.size || 10,
        totalPages: response.totalPages || 0,
        totalElements: response.totalElements || 0,
      };

      setData((prev) => ({ ...prev, [viewKey]: items }));
      setPagination((prev) => ({ ...prev, [viewKey]: paginationData }));
    } catch (error) {
      console.error(`Error fetching ${viewId}:`, error);
      toast.error(`Failed to load data`);
      setData((prev) => ({ ...prev, [viewKey]: [] }));
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (viewId, newPage) => {
    const viewKey = toCamelCase(viewId);
    setPagination((prev) => ({
      ...prev,
      [viewKey]: { ...prev[viewKey], currentPage: newPage },
    }));
    fetchViewData(viewId, newPage, null, null);
  };

  // Handle page size change
  const handlePageSizeChange = (viewId, newSize) => {
    const viewKey = toCamelCase(viewId);
    setPagination((prev) => ({
      ...prev,
      [viewKey]: { ...prev[viewKey], pageSize: newSize, currentPage: 0 },
    }));
    fetchViewData(viewId, 0, newSize, null);
  };

  // Apply filters
  const applyFilters = (viewId, newFilters) => {
    const viewKey = toCamelCase(viewId);
    setFilters((prev) => ({ ...prev, [viewKey]: newFilters }));
    setPagination((prev) => ({
      ...prev,
      [viewKey]: { ...prev[viewKey], currentPage: 0 },
    }));
    fetchViewData(viewId, 0, null, newFilters);
  };

  // Clear filters
  const clearFilters = (viewId) => {
    const viewKey = toCamelCase(viewId);
    let defaultFilters;

    if (viewId.includes("projects")) {
      defaultFilters = { name: "", status: null, sort: "-createdAt" };
    } else if (viewId.includes("sites")) {
      defaultFilters = { name: "", sortBy: "createdAt", sortDir: "desc" };
    } else if (viewId.includes("device-types") || viewId.includes("room-types") || viewId.includes("point-types")) {
      defaultFilters = { name: "" };
    } else {
      defaultFilters = { projId: "", projName: "", pointDisplayName: "", sortBy: "pointId", sortDir: "asc" };
    }

    setFilters((prev) => ({ ...prev, [viewKey]: defaultFilters }));
    setPagination((prev) => ({
      ...prev,
      [viewKey]: { ...prev[viewKey], currentPage: 0 },
    }));
    fetchViewData(viewId, 0, null, defaultFilters);
  };

  // Modal handlers
  const openProjectModal = (mode, project = null) => {
    setProjectModalState({ isOpen: true, mode, selectedProject: project });
  };

  const closeProjectModal = () => {
    setProjectModalState({ isOpen: false, mode: "create", selectedProject: null });
  };

  const handleProjectModalSuccess = () => {
    if (currentView) fetchViewData(currentView);
  };

  const openPointModal = (mode, point = null) => {
    setPointModalState({ isOpen: true, mode, selectedPoint: point });
  };

  const closePointModal = () => {
    setPointModalState({ isOpen: false, mode: "create", selectedPoint: null });
  };

  const handlePointModalSuccess = () => {
    if (currentView) fetchViewData(currentView);
  };

  const openSiteModal = (mode, site = null) => {
    setSiteModalState({ isOpen: true, mode, selectedSite: site });
  };

  const closeSiteModal = () => {
    setSiteModalState({ isOpen: false, mode: "create", selectedSite: null });
  };

  const handleSiteModalSuccess = () => {
    if (currentView) fetchViewData(currentView);
  };

  const openDeviceTypeModal = (mode, deviceType = null) => {
    setDeviceTypeModalState({ isOpen: true, mode, selectedDeviceType: deviceType });
  };

  const closeDeviceTypeModal = () => {
    setDeviceTypeModalState({ isOpen: false, mode: "create", selectedDeviceType: null });
  };

  const handleDeviceTypeModalSuccess = () => {
    if (currentView) fetchViewData(currentView);
  };

  const openRoomTypeModal = (mode, roomType = null) => {
    setRoomTypeModalState({ isOpen: true, mode, selectedRoomType: roomType });
  };

  const closeRoomTypeModal = () => {
    setRoomTypeModalState({ isOpen: false, mode: "create", selectedRoomType: null });
  };

  const handleRoomTypeModalSuccess = () => {
    if (currentView) fetchViewData(currentView);
  };

  const openPointTypeModal = (mode, pointType = null) => {
    setPointTypeModalState({ isOpen: true, mode, selectedPointType: pointType });
  };

  const closePointTypeModal = () => {
    setPointTypeModalState({ isOpen: false, mode: "create", selectedPointType: null });
  };

  const handlePointTypeModalSuccess = () => {
    if (currentView) fetchViewData(currentView);
  };


  // Action handlers
  const handleAdd = (viewId) => {
    const isProjectView = viewId.includes("projects");
    const isSiteView = viewId.includes("sites");
    const isDeviceTypeView = viewId.includes("device-types");
    const isRoomTypeView = viewId.includes("room-types");
    const isPointTypeView = viewId.includes("point-types");

    if (isProjectView) openProjectModal("create");
    else if (isSiteView) openSiteModal("create");
    else if (isDeviceTypeView) openDeviceTypeModal("create");
    else if (isRoomTypeView) openRoomTypeModal("create");
    else if (isPointTypeView) openPointTypeModal("create");
    else openPointModal("create");
  };

  const handleView = (viewId, row) => {
    const isProjectView = viewId.includes("projects");
    const isSiteView = viewId.includes("sites");
    const isDeviceTypeView = viewId.includes("device-types");
    const isRoomTypeView = viewId.includes("room-types");
    const isPointTypeView = viewId.includes("point-types");

    if (isProjectView) openProjectModal("view", row);
    else if (isSiteView) openSiteModal("view", row);
    else if (isDeviceTypeView) openDeviceTypeModal("view", row);
    else if (isRoomTypeView) openRoomTypeModal("view", row);
    else if (isPointTypeView) openPointTypeModal("view", row);
    else openPointModal("view", row);
  };

  const handleEdit = (viewId, row) => {
    const isSiteView = viewId.includes("sites");
    const isDeviceTypeView = viewId.includes("device-types");
    const isRoomTypeView = viewId.includes("room-types");
    const isPointTypeView = viewId.includes("point-types");

    if (isSiteView) openSiteModal("edit", row);
    else if (isDeviceTypeView) openDeviceTypeModal("edit", row);
    else if (isRoomTypeView) openRoomTypeModal("edit", row);
    else if (isPointTypeView) openPointTypeModal("edit", row);
  };

  const handleDelete = (viewId, row) => {
    setPendingDelete({ viewId, row, deleting: false });
  };

  const executePendingDelete = async () => {
    const { viewId, row } = pendingDelete;
    if (!viewId || !row) return;

    const isRoomTypeView = viewId.includes("room-types");
    const isDeviceTypeView = viewId.includes("device-types");
    const isPointTypeView = viewId.includes("point-types");

    setPendingDelete((prev) => ({ ...prev, deleting: true }));
    try {
      if (isRoomTypeView || isDeviceTypeView || isPointTypeView) {
        if (isRoomTypeView) await roomTypeService.remove(row.id);
        else if (isPointTypeView) await pointTypeService.remove(row.id);
        else await deviceTypeService.remove(row.id);
        toast.success("Deleted successfully");
        if (currentView) fetchViewData(currentView);
      } else {
        const viewKey = toCamelCase(viewId);
        setData((prev) => ({
          ...prev,
          [viewKey]: prev[viewKey].filter((item) => item.id !== row.id),
        }));
        toast.success("Deleted successfully");
      }
      setPendingDelete({ viewId: null, row: null, deleting: false });
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to delete");
      }
      setPendingDelete((prev) => ({ ...prev, deleting: false }));
    }
  };

  const handleBulkDelete = (viewId, selectedRows) => {
    const viewKey = toCamelCase(viewId);
    const idsToDelete = selectedRows.map((row) => row.id);
    setData((prev) => ({
      ...prev,
      [viewKey]: prev[viewKey].filter((item) => !idsToDelete.includes(item.id)),
    }));
    toast.success(`Deleted ${selectedRows.length} items`);
  };

  const handleRefresh = (viewId) => {
    fetchViewData(viewId);
  };

  // Render content
  const renderContent = (activeView, viewData) => {
    const viewKey = toCamelCase(activeView || "");
    const isProjectView = activeView?.includes("projects");
    const isSiteView = activeView?.includes("sites");
    const isDeviceTypeView = activeView?.includes("device-types");
    const isRoomTypeView = activeView?.includes("room-types");
    const isPointTypeView = activeView?.includes("point-types");

    // Block non-ADMIN users from type views (guards against direct URL navigation)
    if ((isDeviceTypeView || isRoomTypeView || isPointTypeView) && getCurrentUserType() !== "ADMIN") {
      return (
        <div style={{ padding: "40px", textAlign: "center" }}>
          <h4 style={{ color: "#dc3545" }}>Access Denied</h4>
          <p style={{ color: "#6c757d" }}>This section is only accessible to administrators.</p>
        </div>
      );
    }

    let FilterComponent, PaginationComponent, columns, defaultFilters;

    if (isProjectView) {
      FilterComponent = ProjectsFilter;
      PaginationComponent = ProjectsPagination;
      columns = columnDefinitions.projects;
      defaultFilters = { name: "", status: null, sort: "-createdAt" };
    } else if (isSiteView) {
      FilterComponent = SitesFilter;
      PaginationComponent = SitesPagination;
      columns = columnDefinitions.sites;
      defaultFilters = { name: "", sortBy: "createdAt", sortDir: "desc" };
    } else if (isDeviceTypeView) {
      FilterComponent = DeviceTypesFilter;
      PaginationComponent = SitesPagination;
      columns = columnDefinitions.deviceTypes;
      defaultFilters = { name: "" };
    } else if (isRoomTypeView) {
      FilterComponent = RoomTypesFilter;
      PaginationComponent = SitesPagination;
      columns = columnDefinitions.roomTypes;
      defaultFilters = { name: "" };
    } else if (isPointTypeView) {
      FilterComponent = PointTypesFilter;
      PaginationComponent = SitesPagination;
      columns = columnDefinitions.pointTypes;
      defaultFilters = { name: "" };
    } else {
      FilterComponent = PointsFilter;
      PaginationComponent = PointsPagination;
      columns = columnDefinitions.points;
      defaultFilters = { projId: "", projName: "", pointDisplayName: "", sortBy: "pointId", sortDir: "asc" };
    }

    const currentData = data[viewKey] || [];
    const currentPagination = pagination[viewKey] || { currentPage: 0, pageSize: 10, totalPages: 0, totalElements: 0 };
    const currentFilters = filters[viewKey] || defaultFilters;

    return (
      <>
        <FilterComponent
          filters={currentFilters}
          onApplyFilters={(newFilters) => applyFilters(activeView, newFilters)}
          onClearFilters={() => clearFilters(activeView)}
        />
        <ModuleDataTable
          title={viewData?.name || "Data"}
          data={currentData}
          columns={columns}
          loading={loading}
          searchable={false}
          addButton={isSiteView || isDeviceTypeView || isRoomTypeView || isPointTypeView}
          refreshButton={true}
          actions={{
            onAdd: () => handleAdd(activeView),
            onView: (row) => handleView(activeView, row),
            onEdit: (isSiteView || isDeviceTypeView || isRoomTypeView || isPointTypeView) ? (row) => handleEdit(activeView, row) : undefined,
            onDelete: (isRoomTypeView || isDeviceTypeView || isPointTypeView) ? (row) => handleDelete(activeView, row) : undefined,
            onBulkDelete: (rows) => handleBulkDelete(activeView, rows),
            onRefresh: () => handleRefresh(activeView),
            ...(isProjectView && {
              onSyncProject: (row) => handleSyncProject(row),
              isSyncingProject: (row) => !!(row.projId && syncingProjectIds.has(row.projId)),
            }),
          }}
        />
        <PaginationComponent
          pagination={currentPagination}
          onPageChange={(newPage) => handlePageChange(activeView, newPage)}
          onPageSizeChange={(newSize) => handlePageSizeChange(activeView, newSize)}
        />
      </>
    );
  };

  return (
    <>
      <ModuleLayout
        moduleTitle="Devices Management"
        views={views}
        defaultView="my-projects"
        renderContent={renderContent}
        breadcrumbItems={{
          parent: "Dashboard",
          title: "Devices Management",
        }}
        hideViewSelector={true}
        initialView={urlView || "my-projects"}
      />

      <ProjectModal
        isOpen={projectModalState.isOpen}
        toggle={closeProjectModal}
        mode={projectModalState.mode}
        projectData={projectModalState.selectedProject}
        onSuccess={handleProjectModalSuccess}
      />

      <PointModal
        isOpen={pointModalState.isOpen}
        toggle={closePointModal}
        mode={pointModalState.mode}
        pointData={pointModalState.selectedPoint}
        onSuccess={handlePointModalSuccess}
      />

      <SiteModal
        isOpen={siteModalState.isOpen}
        toggle={closeSiteModal}
        mode={siteModalState.mode}
        siteData={siteModalState.selectedSite}
        onSuccess={handleSiteModalSuccess}
      />

      <DeviceTypeModal
        isOpen={deviceTypeModalState.isOpen}
        toggle={closeDeviceTypeModal}
        mode={deviceTypeModalState.mode}
        deviceTypeData={deviceTypeModalState.selectedDeviceType}
        onSuccess={handleDeviceTypeModalSuccess}
      />

      <RoomTypeModal
        isOpen={roomTypeModalState.isOpen}
        toggle={closeRoomTypeModal}
        mode={roomTypeModalState.mode}
        roomTypeData={roomTypeModalState.selectedRoomType}
        onSuccess={handleRoomTypeModalSuccess}
      />

      <PointTypeModal
          isOpen={pointTypeModalState.isOpen}
          toggle={closePointTypeModal}
          mode={pointTypeModalState.mode}
          pointTypeData={pointTypeModalState.selectedPointType}
          onSuccess={handlePointTypeModalSuccess}
      />

      <ConfirmDeleteDialog
        isOpen={!!pendingDelete.row}
        onClose={() => setPendingDelete({ viewId: null, row: null, deleting: false })}
        onConfirm={executePendingDelete}
        deleting={pendingDelete.deleting}
        title="Delete Item"
        itemName={pendingDelete.row?.name}
      />

    </>
  );
};

export default DevicesManagementModule;
